import './settlements.scss';

import { requestSettlement } from '@basqet-dashboard/common/api';
import { Button, Dropdown, FormInput, Modal } from '@basqet-dashboard/common/components';
import { ACCOUNT_TYPES } from '@basqet-dashboard/common/config';
import { useCurrencies } from '@basqet-dashboard/common/hooks';
import {
  useFetchCollectionsWallets,
  useFetchFeeMerchant,
  useFetchSettlementAccounts
} from '@basqet-dashboard/common/hooks/queries';
import {
  formatCryptoCurrency,
  formatErrorMessage,
  formatIconUrl,
  formatWallet,
  getCurrency
} from '@basqet-dashboard/common/utils';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { toast } from 'react-toastify';

const defaultRequest = {
  account_id: '',
  amount: ''
};
const RequestSettlement = ({ onModalClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [request, setRequest] = useState(defaultRequest);
  const [amountError, setAmountError] = useState('');
  const [amountInWallet, setAmountInWallet] = useState('');
  const [settlementFee, setSettlementFee] = useState(0);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [feeValueType, setFeeValueType] = useState(''); // PERCENTAGE or FIXED
  const [account, setAccount] = useState(null);

  const { accounts } = useFetchSettlementAccounts();
  // We currenly only support FIAT settlement requests. Hence the hard coding
  const FEE_TYPE = 'SETTLEMENT_FIAT';
  const { fee } = useFetchFeeMerchant(FEE_TYPE);
  const { wallets } = useFetchCollectionsWallets();
  const { availableCurrencies } = useCurrencies();

  const accountDropdown = accounts.map((account) => {
    return {
      label: `${account?.currency?.slug} (${formatWallet(account.account_address)})`,
      value: account.id
    };
  });

  const handleAccountChange = async (e) => {
    const { name, value } = e.target;
    const account = accounts.find((_account) => _account.id === +value);
    const settlementPercent = ACCOUNT_TYPES.find(
      (_account_type) => _account_type.value === account?.account_type
    );
    const wallet = wallets.find((_wallet) => _wallet.currency_id === account?.currency_id);

    setSelectedCurrency(() => getCurrency(availableCurrencies, account?.currency.id));
    setAccount(account);

    setSettlementFee(fee?.value || settlementPercent?.settlement_percent);
    setFeeValueType(fee?.value_type);
    setAmountInWallet(wallet?.available_balance);

    setRequest({ ...request, [name]: value, amount: '' });
    setAmountError('');
  };

  const validateAmount = (value) => {
    if (+value === 0) {
      setAmountError('Kindly enter an amount greater zero');
    } else if (value > amountInWallet) {
      setAmountError(
        'The amount you want to request cannot be more than your balance for this currency'
      );
    } else {
      setAmountError('');
    }
  };

  const handleAmountChange = (e) => {
    const { value } = e.target;
    validateAmount(value);
    handleChange(e);
  };

  const handleChange = (e) => {
    const { value, name } = e.target;
    setRequest({ ...request, [name]: value });
  };

  const requestSettlementAction = async () => {
    setIsLoading(true);
    try {
      await requestSettlement(request);
      await toast.success('Settlement Requested Successfully');

      // Ensure that the toast shows before reloading the page
      setTimeout(() => {
        location.reload();
      }, 1000);

      onModalClose();
    } catch (error) {
      formatErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const setAmountToMax = () => {
    if (amountInWallet > 0) {
      setAmountError('');
    }
    setRequest({ ...request, amount: formatCryptoCurrency(amountInWallet) });
  };

  const feeDisplaySuffix = () => {
    if (feeValueType === 'FIXED') {
      return account?.currency?.slug;
    }
    return '%';
  };

  const calculateSettlementAmount = () => {
    if (feeValueType === 'FIXED') {
      return request?.amount - settlementFee;
    }

    return formatCryptoCurrency(request?.amount * (settlementFee / 100));
  };

  return (
    <Modal onModalClose={onModalClose} header={{ name: 'Request', cursive: 'settlement' }}>
      <div className="settlements__form">
        <div className="settlements__form__group">
          <label htmlFor="account_id" className="form__label">
            Settlement Address
          </label>
          <Dropdown
            placeholder="Settlement Address"
            name="account_id"
            id="account_id"
            options={accountDropdown}
            register={{ onChange: handleAccountChange }}
            label="Settlement Address"
          />

          {amountInWallet !== '' && (
            <>
              <div className="amount__in__wallet">
                <p>Amount in Wallet</p>
                <h3 className="amount__in__wallet__value">
                  <img
                    src={formatIconUrl(selectedCurrency?.icon_url, selectedCurrency?.slug)}
                    alt="Naira"
                    className="amount__in__wallet__value--icon"
                  />{' '}
                  <span>~{formatCryptoCurrency(amountInWallet)}</span>
                </h3>
              </div>

              <FormInput
                placeholder="Amount"
                name="amount"
                type="number"
                label="Request Amount"
                register={{ onChange: handleAmountChange }}
                value={request?.amount}
                error={amountError}
                autofocus
              />
              <div className="settlement__fee">
                <p>
                  Settlement Fee ({settlementFee}
                  {feeDisplaySuffix()})
                </p>
                <p>
                  <b>
                    {!!request.amount && `${calculateSettlementAmount()} ${selectedCurrency?.slug}`}
                  </b>
                </p>
              </div>

              <div className="settlement__form__button__group">
                <Button isFull onClick={setAmountToMax} variant="outline">
                  Request Max{' '}
                </Button>
                <Button
                  isFull
                  isLoading={isLoading}
                  onClick={requestSettlementAction}
                  disabled={Boolean(amountError) || !request?.amount}>
                  Request Settlement
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

RequestSettlement.propTypes = {
  onModalClose: PropTypes.func
};

export { RequestSettlement };
